import { TouchEvent, useCallback, useEffect, useRef, useState } from 'react';

// Touch-and-hold duration (ms) before a row's context menu opens on mobile.
// Matches the platform-typical 500ms used for long-press selection so it
// feels native.
const LONG_PRESS_DELAY_MS = 500;
// Movement threshold (px) past which a touch is treated as a scroll
// gesture and the pending long-press is cancelled.
const LONG_PRESS_MOVE_THRESHOLD_PX = 8;
// Window (ms) after a long-press fires within which the synthesized click is
// swallowed. Past this, a stale `fired` flag must not eat a genuine tap.
const LONG_PRESS_CONSUME_WINDOW_MS = 700;

// Local replacement for the host `useIsBelowMd` (was imported from
// `./TerminalDrawer`). There is no package seam for the host's
// responsive/mobile-sidebar state, so we derive it directly from a media
// query. FLAG: in the host this drove `setSidebarCollapsed(true)` after a
// selection on mobile; that drawer behavior is host-owned and dropped
// here (see `collapseSidebarOnMobile`). This hook is retained only because
// the long-press / mobile context-menu UX lives purely within this
// component and benefits from knowing the viewport width.
export function useIsBelowMd(): boolean {
  const query = '(max-width: 768px)';
  const [isBelowMd, setIsBelowMd] = useState<boolean>(() => {
    if (typeof window === 'undefined' || window.matchMedia == null) {
      return false;
    }
    return window.matchMedia(query).matches;
  });
  useEffect(() => {
    if (typeof window === 'undefined' || window.matchMedia == null) return;
    const mql = window.matchMedia(query);
    const onChange = () => setIsBelowMd(mql.matches);
    onChange();
    mql.addEventListener('change', onChange);
    return () => mql.removeEventListener('change', onChange);
  }, []);
  return isBelowMd;
}

// Long-press handler shared by SessionRow and FolderRow so touch users
// can open the same context menu desktop users get from right-click /
// hovering the ellipsis. Returns touch handlers to spread onto the row
// plus a `consumeFired()` for the row's onClick to skip when a press
// just resolved (otherwise lifting the finger would also fire the row
// click, e.g. selecting the chat we just opened the menu for).
export function useLongPress(
  onLongPress: (coords: { x: number; y: number }) => void,
) {
  const timerRef = useRef<number | null>(null);
  const startRef = useRef<{ x: number; y: number } | null>(null);
  const firedRef = useRef(false);
  const firedAtRef = useRef(0);

  const cancel = useCallback(() => {
    if (timerRef.current != null) {
      window.clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const onTouchStart = useCallback(
    (e: TouchEvent<HTMLElement>) => {
      const t = e.touches[0];
      if (t == null) return;
      startRef.current = { x: t.clientX, y: t.clientY };
      firedRef.current = false;
      cancel();
      timerRef.current = window.setTimeout(() => {
        const start = startRef.current;
        if (start == null) return;
        firedRef.current = true;
        firedAtRef.current = Date.now();
        onLongPress(start);
      }, LONG_PRESS_DELAY_MS);
    },
    [cancel, onLongPress],
  );

  const onTouchMove = useCallback(
    (e: TouchEvent<HTMLElement>) => {
      const start = startRef.current;
      if (start == null) return;
      const t = e.touches[0];
      if (t == null) return;
      if (
        Math.abs(t.clientX - start.x) > LONG_PRESS_MOVE_THRESHOLD_PX ||
        Math.abs(t.clientY - start.y) > LONG_PRESS_MOVE_THRESHOLD_PX
      ) {
        cancel();
      }
    },
    [cancel],
  );

  const onTouchEnd = useCallback(() => cancel(), [cancel]);

  const consumeFired = useCallback(() => {
    if (!firedRef.current) return false;
    firedRef.current = false;
    // Only swallow the click synthesized immediately after the long-press.
    // A stale flag (e.g. the synthesized click was lost because the user
    // dismissed elsewhere) must not eat a genuine later tap on the same row.
    return Date.now() - firedAtRef.current < LONG_PRESS_CONSUME_WINDOW_MS;
  }, []);

  return {
    touchHandlers: {
      onTouchStart,
      onTouchMove,
      onTouchEnd,
      onTouchCancel: onTouchEnd,
    },
    consumeFired,
  };
}
